import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchBillableMatters } from '../../hooks/useFetchBillableMatters';
import { Spin } from '../Ant';
import { useInView } from 'react-intersection-observer';
import NoData from '../dashboard/noData';
import { BriefcaseEmpty } from '../icons';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import InvoiceMatterCard from './invoiceMatterCard';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { useSearchParams } from 'react-router-dom';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const BillableMatterListing = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { push } = useNavigationStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('billableSearch') || '';
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const billableMattersSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'openingDate',
        label: t('matter:openingDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      { field: 'subject', label: t('matter:subject'), type: 'text', defaultDirection: 'ASC' },
      {
        field: 'matterNumber',
        label: t('matter:matterNumber'),
        type: 'number',
        defaultDirection: 'ASC',
      },
      {
        field: 'principal.name',
        label: t('matter:principal'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'openingDate',
    direction: 'DESC',
  });
  const {
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: paginatedMatterData,
    isLoading: isBillableMattersLoading,
  } = useFetchBillableMatters({
    search: searchValue,
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    limit: 10,
    relations: ['principalCompany:name', 'principalPrivatePerson:fullName'],
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'currency',
      'sumOfBillableEntriesInCurrency',
      'sumOfBillableEntries',
      'periodStartDate',
      'periodEndDate',
      'hasImportantNotes',
      'hasImportantRelatedNotes',
      'hasNotes',
    ],
  });

  const handleGotoBillableMatterDetail = (matterId: string) => {
    push(ROUTES.BILLABLE_MATTER_DETAIL + `/${matterId}`);
  };
  useEffect(() => {
    setSearchParams({
      billableSearch: searchValue,
    });
  }, [searchValue]);
  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && paginatedMatterData?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="billable-matter-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('matter:search')}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: ['billable-matters-list'],
            });
            return refetch();
          }}
          searchValue={searchValue}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={billableMattersSortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {paginatedMatterData &&
        paginatedMatterData.length > 0 &&
        paginatedMatterData.map((item) => {
          return (
            <InvoiceMatterCard
              key={item?.id}
              matterStatus={item?.status}
              subject={item?.subject ?? ''}
              matterInvoiceableType={item?.invoiceableType}
              number={item?.matterNumber ?? ''}
              customer={
                item?.principalCompany?.name ?? item?.principalPrivatePerson?.fullName ?? '-'
              }
              startDate={item?.periodStartDate}
              endDate={item?.periodEndDate}
              amount={item?.sumOfBillableEntries ?? 0}
              amountInCurrency={item?.sumOfBillableEntriesInCurrency ?? 0}
              currency={item?.currency}
              hasImportantNotes={item?.hasImportantNotes}
              hasImportantRelatedNotes={item?.hasImportantRelatedNotes}
              hasNotes={item?.hasNotes}
              onSelect={() => handleGotoBillableMatterDetail(item?.id)}
            />
          );
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!paginatedMatterData ||
        (Object.entries(paginatedMatterData).length === 0 && !isBillableMattersLoading)) && (
        <Flex className="flex-grow" justify="center" align="center">
          <NoData
            header={t('matter:noMatters')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<BriefcaseEmpty />}
          />
        </Flex>
      )}
      {isBillableMattersLoading ? (
        <SkeletonCardList rows={[1, 1, 2]} withSubcontentOnFirstRow />
      ) : (
        renderLoader()
      )}
    </CollapsibleListLayout>
  );
};

export default BillableMatterListing;
